/**
 * @file Renders the modal that edits an organization's profile fields, including its custom link templates.
 */

import React from "react";
import { useOutletContext } from "react-router-dom";

import { UpdateModal } from "@components/Page";
import type { ModalRef } from "@components/Page/Modal/types";
import { updateMyOrganizationOperation as operation } from "@api/services/organizations";

import getUpdateInputs from "./helpers/getUpdateInputs";

const LABEL_ENTITY = "Organization";
const LABEL_TITLE = `Edit ${LABEL_ENTITY}`;
const LABEL_SUBMIT = "Save";
const EMPTY_SECRET = "";

type Organization = Record<string, unknown> & {
  emailFooterSource?: string;
  isEmailVerified?: boolean;
  docusignWebhookSecret?: string;
};

type OutletContext = {
  indexSponsors: () => Promise<void>;
  updateOrganization: (organization: Organization) => void;
};

type Props = {
  title?: string;
  onlyFieldNames?: string[];
  modal: ModalRef;
  organization: Organization;
};

/**
 * Renders the form that updates the current organization and its custom link templates.
 */
const OrganizationUpdateModal = ({
  title = LABEL_TITLE,
  onlyFieldNames = [],
  modal,
  organization,
}: Props): JSX.Element => {
  const { indexSponsors, updateOrganization } = useOutletContext<OutletContext>();

  const organizationWithHiddenSecret = {
    ...organization,
    docusignWebhookSecret: EMPTY_SECRET,
  };

  const inputs = getUpdateInputs(organizationWithHiddenSecret, onlyFieldNames);
  const schema = operation.getSchema(inputs);

  /**
   * Refreshes the sponsor list and applies the organization returned by the form.
   */
  const onSuccess = async (newOrganization: Organization) => {
    await indexSponsors();
    updateOrganization(newOrganization);
  };

  const updateModalProps = {
    item: organizationWithHiddenSecret,
    entity: LABEL_ENTITY,
    submitLabel: LABEL_SUBMIT,
    modal,
    title,
    schema,
    onSuccess,
    operation,
  };

  return <UpdateModal {...updateModalProps} />;
};

export default OrganizationUpdateModal;
